import { FC, useEffect } from 'react'
import { BreadCrumb } from 'components/BreadCrumb/BreadCrumb'
import { ContentLayout } from 'components/Layouts'
import { useTranslation } from 'react-i18next'
import { getMedicareAdvantageStatus } from 'redux/reducers/users'
import { useDispatch, useSelector } from 'react-redux'
import { MyLink } from 'components/MyLink'
import { useMedicareUrl } from 'hooks/useMedicareUrl'
import { getPreventiveServicesList, getPreventiveServices, getStatus, Status } from 'redux/reducers/preventive-services'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { Alert } from '@cmsgov/ds-medicare-gov'
import { PreventiveServicesContent } from './components/PreventiveServicesContent'
import { TabRoutes } from 'components/TabRoutes'
import { Divider } from 'components/Divider/Divider'
import { buildAbsoluteMedicareMyUrl } from 'utils/urls/buildAbsoluteMedicareMyUrl'
import { LaunchDarklyDependentContent } from 'components/LaunchDarklyDependentContent/LaunchDarklyDependentContent'
import { SortDirection } from 'models/preventive-services'
import { Component } from '@cms-max/medicare-api-sdk'
import { useFindProviderUrl } from 'hooks/useFindProviderUrl'

export const PreventiveServices: FC = () => {
  const { t } = useTranslation('preventiveServices')
  const dispatch = useDispatch()
  const status = useSelector(getStatus)
  const displayMAContent = useSelector(getMedicareAdvantageStatus)
  const preventiveServicesData = useSelector(getPreventiveServicesList)
  const isLoading = status === Status.LOADING
  const isError = status === Status.ERROR
  const coveredPrevServicesLink = useMedicareUrl(
    'https://www.medicare.gov/coverage/preventive-screening-services',
    'https://es.medicare.gov/coverage/preventive-screening-services'
  )
  const backLinkLegacy = useMedicareUrl(
    'https://www.medicare.gov/mbp/Dashboard.aspx',
    'https://www.medicare.gov/mbp/Dashboard.aspx?lang=es-mx'
  )
  const {
    findProviderUrl,
    findProviderUrlWithoutAddress
  } = useFindProviderUrl()

  const tabConfig = {
    tabConfig: [
      {
        route: 'available',
        label: t('availableNow'),
        content: <PreventiveServicesContent data={preventiveServicesData.filter(service => service.availableNow === true)} emptyMessage={t('emptyAvailableNow')} sortDirection={SortDirection.DESC} />
      },
      {
        route: 'upcoming',
        label: t('upcoming'),
        content: <PreventiveServicesContent data={preventiveServicesData.filter(service => service.availableNow === false)} emptyMessage={t('emptyUpcoming')} sortDirection={SortDirection.ASC} />
      }
    ],
    defaultRedirect: 'available'
  }

  useEffect(() => {
    if (!displayMAContent) {
      dispatch(getPreventiveServices())
    }
  }, [dispatch, displayMAContent])

  return (
    <>
      <LaunchDarklyDependentContent
        flagName='useNewAccountHomeLinks'
        fallbackContent={(
          <BreadCrumb
            backButtonText={t('breadcrumbs.back')}
            width='wide'
            navAriaLabel={t('breadcrumb.back')}
            backButtonLink={backLinkLegacy}
            linkIsExternal={true}
            siteActivityProps={{
              core: {
                component: Component.PreventiveServices,
                elementId: 'ps_backtoaccounthome_click',
                coreClickData: {
                  linkText: t('breadcrumb.back'),
                  linkUrl: backLinkLegacy
                }
              }
            }}
          />
      )}>
        <BreadCrumb
          backButtonText={t('breadcrumbs.back')}
          width='wide'
          navAriaLabel={t('breadcrumb.back')}
          backButtonLink='/home'
          siteActivityProps={{
            core: {
              component: Component.PreventiveServices,
              elementId: 'ps_backtoaccounthome_click',
              coreClickData: {
                linkText: t('breadcrumb.back'),
                linkUrl: buildAbsoluteMedicareMyUrl('/home')
              }
            }
          }}
        />
      </LaunchDarklyDependentContent>
      <ContentLayout
        className="ds-u-padding-bottom--7"
        width="wide"
        heading={t('heading')}
        withPrintButton
        printComponent={Component.PreventiveServices}
      >
        <div className="ds-l-container">
          {displayMAContent && (
            <>
              <p>{t('maPlanContent')}</p>
              <p>
                <MyLink href={coveredPrevServicesLink}
                  siteActivityProps={{
                    core: {
                      component: Component.PreventiveServices,
                      elementId: 'ps_learncoverage_click',
                      coreClickData: {
                        linkText: t('maPlanContentLink'),
                        linkUrl: buildAbsoluteMedicareMyUrl('/preventive-services')
                      }
                    }
                  }}
                >
                  {t('maPlanContentLink')}
                </MyLink>
              </p>
            </>
          )}
          {(isLoading && !displayMAContent && !isError) && (
            <MySpinner />
          )}
          {(!displayMAContent && !isLoading && !isError) && (
            <>
              <LaunchDarklyDependentContent
                flagName='usePreventiveServicesCcxpIntegration'
                fallbackContent={<p>{t('subHeading')}</p>}
              >
                <p>
                  {t('subHeadingCCXP.textBeforeLink')}{' '}
                  <MyLink
                    href={findProviderUrl}
                    siteActivityProps={{
                      core: {
                        component: Component.PreventiveServices,
                        elementId: 'ps_findandcompareproviders_click',
                        coreClickData: {
                          linkText: t('subHeadingCCXP.linkText'),
                          linkUrl: findProviderUrlWithoutAddress
                        }
                      }
                    }}
                  >
                    {t('subHeadingCCXP.linkText')}
                  </MyLink>
                  {t('subHeadingCCXP.textAfterLink')}
                </p>
              </LaunchDarklyDependentContent>
              <TabRoutes config={tabConfig}></TabRoutes>
            </>
          )}
          {isError && (
            <Alert variation='warn' role='alert' hideIcon={false}>
              <p className="ds-c-alert__text">{t('errorMessage')}</p>
            </Alert>
          )}
          <Divider />
          <h2>{t('footerHeading')}</h2>
          <p>{t('footerText')}</p>
        </div>
      </ContentLayout>
    </>
  )
}
